import { useState, useMemo } from 'react';
import { Box, Tooltip, Typography } from '@forgeglobal/design-framework';
import { ClickAwayListener } from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Table from '@tcs/client-portal-common/components/molecules/Table';
import BaseHeader from '@tcs/client-portal-common/components/molecules/Table/cells/headers/BaseHeader';
import BaseCell from '@tcs/client-portal-common/components/molecules/Table/cells/BaseCell';
import DownloadActionCell from './DownloadActionCell';
import { ReportType } from '@tcs/client-portal-api/types/ReportType';

export interface Report {
  id: string;
  dateRange: string;
  dateGenerated: string;
  reportName: string;
  reportCreation?: string;
  downloadUrl?: string;
  startTimestamp?: number;
  reportType: string;
  dateGeneratedTimestamp?: number;
}

interface ReportsTableProps {
  reports: Report[];
  onDownload?: (reportId: string, format: string) => void;
  onRowClick?: (report: Report) => void;
  onColumnClick?: (columnId: string, sortDirection?: 'asc' | 'desc') => void;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
}

const MAX_VISIBLE_LENGTH = 30;

const ReportsTable = ({
  reports,
  onRowClick,
  onColumnClick,
  sortColumn,
  sortDirection,
}: ReportsTableProps) => {
  const [reportNameTooltipOpen, setReportNameTooltipOpen] = useState(false);
  const [reportCreationTooltipOpen, setReportCreationTooltipOpen] =
    useState(false);

  const baseColumns = useMemo(
    () => [
      {
        accessor: 'dateGenerated',
        header: () => <BaseHeader text="Date Generated" align="left" />,
        cell: (info) => <BaseCell value={info.getValue()} />,
        size: 200,
        sortable: true,
      },
      {
        accessor: 'reportName',
        header: () => (
          <BaseHeader
            text={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Report Name
                <ClickAwayListener
                  onClickAway={() => setReportNameTooltipOpen(false)}>
                  <Tooltip
                    open={reportNameTooltipOpen}
                    onOpen={() => setReportNameTooltipOpen(true)}
                    onClose={() => setReportNameTooltipOpen(false)}
                    title={
                      <Box>
                        <Typography variant="body2">
                          <strong>Accounts Report:</strong> Overview of account
                          balances, fee status, RMDs, and address indicators.
                        </Typography>
                        <Typography variant="body2">
                          <strong>Positions Report:</strong> Investment holdings
                          by account, including units, valuations, maturity, and
                          distress indicators.
                        </Typography>
                        <Typography variant="body2">
                          <strong>Transaction Report:</strong> Full transaction
                          history with asset IDs and recurring distributions.
                        </Typography>
                        <Typography variant="body2">
                          <strong>Beneficiary Report:</strong> Beneficiary
                          names, types, levels, and share percentages.
                        </Typography>
                      </Box>
                    }
                    arrow>
                    <InfoOutlined
                      sx={{
                        fontSize: '1rem',
                        opacity: 0.5,
                        cursor: 'pointer',
                      }}
                      onClick={() => setReportNameTooltipOpen(true)}
                    />
                  </Tooltip>
                </ClickAwayListener>
              </Box>
            }
          />
        ),
        cell: (info) => {
          const report = info.row.original;
          const reportName: string = report.reportName || '';
          const dateRange: string = report.dateRange || '';
          const truncate = reportName && reportName.length > MAX_VISIBLE_LENGTH;
          const displayReportName = truncate
            ? `${reportName.substring(0, MAX_VISIBLE_LENGTH)}...`
            : reportName;
          return (
            <Tooltip
              title={truncate ? report.reportName : ''}
              placement="top"
              arrow>
              <Box sx={{ cursor: 'pointer', display: 'inline-block' }}>
                <BaseCell
                  value={
                    report.reportType === ReportType.TRANSACTION
                      ? displayReportName + ' ' + dateRange
                      : displayReportName
                  }
                />
              </Box>
            </Tooltip>
          );
        },
        size: 400,
      },
      {
        accessor: 'actions',
        header: () => (
          <Box sx={{ width: '100%', paddingLeft: '10px' }}>
            <BaseHeader text="Actions" align="left" />
          </Box>
        ),
        cell: (info) => {
          const report = info.row.original;
          return (
            <DownloadActionCell
              downloadUrl={report.downloadUrl}
              reportName={report.reportName}
            />
          );
        },
        size: 200,
      },
    ],
    [reportNameTooltipOpen]
  );

  const columns = useMemo(() => {
    const cols = [...baseColumns];

    const reportCreationColumn = {
      accessor: 'reportCreation',
      header: () => (
        <BaseHeader
          text={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              Report Creation
              <ClickAwayListener
                onClickAway={() => setReportCreationTooltipOpen(false)}>
                <Tooltip
                  open={reportCreationTooltipOpen}
                  onOpen={() => setReportCreationTooltipOpen(true)}
                  onClose={() => setReportCreationTooltipOpen(false)}
                  title={
                    <Typography variant="body2">
                      Indicates whether this report was previously scheduled or
                      manually created with custom inputs.
                    </Typography>
                  }
                  arrow>
                  <InfoOutlined
                    sx={{
                      fontSize: '1rem',
                      opacity: 0.5,
                      cursor: 'pointer',
                    }}
                    onClick={() => setReportCreationTooltipOpen(true)}
                  />
                </Tooltip>
              </ClickAwayListener>
            </Box>
          }
        />
      ),
      cell: (info) => {
        const value = info.getValue();
        return <BaseCell value={value || ''} />;
      },
      size: 200,
    };
    cols.splice(cols.length - 1, 0, reportCreationColumn);

    return cols;
  }, [baseColumns, reportCreationTooltipOpen]);

  return (
    <Table
      columns={columns}
      data={reports}
      onRowClick={onRowClick}
      onColumnClick={onColumnClick}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      defaultSortColumn="dateGenerated"
      defaultSortDirection="desc"
    />
  );
};

export default ReportsTable;
